import React, { useContext, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { ApiContext } from '@api/api';
import {
  PageResponseSecurityUsageUserOutDTO,
  SecurityUsageUserFilterInDTO,
  SecurityUsageUserOutDTO,
} from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  filter: SecurityUsageUserFilterInDTO | null;
  title?: string;
}

const PAGE_SIZE = 25;

type ActiveFilter = 'any' | 'active' | 'inactive';

interface LocalUserFilter {
  name: string;
  email: string;
  companyId: string;
  active: ActiveFilter;
}

const EMPTY_LOCAL: LocalUserFilter = { name: '', email: '', companyId: '', active: 'any' };

const buildFilter = (base: SecurityUsageUserFilterInDTO, local: LocalUserFilter): SecurityUsageUserFilterInDTO => {
  const name = local.name.trim();
  const email = local.email.trim();
  const companyId = local.companyId.trim();
  return {
    ...base,
    name: name || undefined,
    email: email || undefined,
    companyId: companyId || undefined,
    active: local.active === 'any' ? undefined : local.active === 'active',
  };
};

const SecurityUsersDialog: React.FC<ComponentProps> = ({ visible, onHide, filter, title }) => {
  const { getUsersBySecurity } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [data, setData] = useState<PageResponseSecurityUsageUserOutDTO | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [inputs, setInputs] = useState<LocalUserFilter>(EMPTY_LOCAL);
  const [appliedLocal, setAppliedLocal] = useState<LocalUserFilter>(EMPTY_LOCAL);
  const acRef = useRef<AbortController | null>(null);

  // Reset local filter + page when the base filter / open state changes.
  useEffect(() => {
    if (!visible || !filter) return;
    setInputs(EMPTY_LOCAL);
    setAppliedLocal(EMPTY_LOCAL);
    setPageNumber(1);
    setData(undefined);
  }, [visible, filter]);

  useEffect(() => {
    if (!visible || !filter) return;
    const ac = new AbortController();
    acRef.current?.abort();
    acRef.current = ac;
    setLoading(true);
    const effective = buildFilter(filter, appliedLocal);
    getUsersBySecurity(
      { pageNumber: pageNumber - 1, pageSize: PAGE_SIZE },
      effective,
      d => {
        if (ac.signal.aborted) return;
        setData(d);
        setLoading(false);
      },
      null,
      {
        onError: (_error, showStandardError) => {
          if (!ac.signal.aborted) setLoading(false);
          showStandardError();
        },
      },
    );
    return () => ac.abort();
  }, [visible, filter, appliedLocal, pageNumber]);

  const onPageChange = (e: { page: number }) => setPageNumber(e.page + 1);

  const applyFilter = () => {
    setPageNumber(1);
    setAppliedLocal(inputs);
  };

  const clearFilter = () => {
    setPageNumber(1);
    setInputs(EMPTY_LOCAL);
    setAppliedLocal(EMPTY_LOCAL);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') applyFilter();
  };

  const hasActiveLocal =
    appliedLocal.name.trim() !== '' ||
    appliedLocal.email.trim() !== '' ||
    appliedLocal.companyId.trim() !== '' ||
    appliedLocal.active !== 'any';

  const activeOptions = [
    { label: tr('SecurityUsersDialog.activeAny', 'Any'), value: 'any' as ActiveFilter },
    { label: tr('SecurityUsersDialog.activeYes', 'Active'), value: 'active' as ActiveFilter },
    { label: tr('SecurityUsersDialog.activeNo', 'Inactive'), value: 'inactive' as ActiveFilter },
  ];

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={title ?? tr('SecurityUsersDialog.title', 'Users')}
      style={{ width: '1100px', maxWidth: '95vw' }}
      blockScroll
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-row gap-3 items-end flex-wrap border rounded p-3 bg-gray-50">
          <div className="flex flex-col">
            <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsersDialog.filterName', 'Name')}</label>
            <InputText value={inputs.name} onChange={e => setInputs({ ...inputs, name: e.target.value })} onKeyDown={onKeyDown} />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsersDialog.filterEmail', 'Email')}</label>
            <InputText value={inputs.email} onChange={e => setInputs({ ...inputs, email: e.target.value })} onKeyDown={onKeyDown} placeholder="user@example.com" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsersDialog.filterCompanyId', 'Company ID')}</label>
            <InputText value={inputs.companyId} onChange={e => setInputs({ ...inputs, companyId: e.target.value })} onKeyDown={onKeyDown} placeholder="UUID" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsersDialog.filterActive', 'Activity')}</label>
            <Dropdown
              value={inputs.active}
              options={activeOptions}
              onChange={e => setInputs({ ...inputs, active: e.value as ActiveFilter })}
            />
          </div>
          <RingilButton mode={'filled-rectangle'} label={tr('SecurityUsersDialog.apply', 'Apply')} onClick={applyFilter} />
          {hasActiveLocal && (
            <RingilButton mode={'text-only'} label={tr('SecurityUsersDialog.clear', 'Clear')} onClick={clearFilter} />
          )}
        </div>

        {loading && !data && <div className="italic text-content-secondary p-4">{tr('SecurityUsersDialog.loading', 'Loading…')}</div>}
        {data && (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-content-secondary">
              {tr('SecurityUsersDialog.total', 'Total')}: <span className="font-bold">{data.total}</span>
            </div>
            {data.data.length === 0 ? (
              <div className="italic text-content-secondary">{tr('SecurityUsersDialog.empty', 'No users.')}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-content-secondary border-b">
                    <th className="py-1 pr-2">{tr('SecurityUsersDialog.name', 'Name')}</th>
                    <th className="py-1 pr-2">{tr('SecurityUsersDialog.email', 'Email')}</th>
                    <th className="py-1 pr-2">{tr('SecurityUsersDialog.company', 'Company')}</th>
                    <th className="py-1 pr-2">{tr('SecurityUsersDialog.active', 'Active')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((u: SecurityUsageUserOutDTO) => (
                    <tr key={u.id} className="border-b last:border-b-0">
                      <td className="py-1 pr-2">{u.fullName ?? '-'}</td>
                      <td className="py-1 pr-2 font-mono">{u.email ?? '-'}</td>
                      <td className="py-1 pr-2">{u.companyName ?? '-'}</td>
                      <td className="py-1 pr-2">
                        {u.active ? (
                          <span className="text-green-700">✓</span>
                        ) : (
                          <span className="text-red-700">✗</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {data.total > PAGE_SIZE && (
              <Paginator
                first={(pageNumber - 1) * PAGE_SIZE}
                rows={PAGE_SIZE}
                totalRecords={data.total}
                onPageChange={onPageChange}
              />
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default SecurityUsersDialog;